import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CompanyContact from './CompanyContact';
import Tag from './../../Tag';
import { isDateExpired } from './../../../services/date';
import { AccountType } from './../../../services/utils/types';
import CompanyBoardPreview from './BoardPreview';
import CompanySlideHeader from './CompanySlideHeader';
import { useAccount } from './../../../contexts/Account';
import DefaultAdvisorAvatar from './../../../icons/user-avatar.svg';
import AcPlusPreview from './AcPlusPreview';
import LeaveBoardButton from './LeaveBoardButton';
import HowItWorks from './HowItWorks';
import BoardJoinButton from './../BoardJoinButton';
import BenefitCard from '../../BenefitCard';
import StaticTile from './StaticTile';

const useStyles = (isSM, disabledCompany) => ({
  card: {
    width: '100%',
    overflowY: disabledCompany ? 'hidden' : null,
    paddingLeft: !isSM ? 0 : 2,
    paddingRight: !isSM ? 0 : 2,
  },
  companyInformationSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: isSM ? '0px' : '40px',
    paddingRight: isSM ? '0px' : '20px',
    paddingTop: isSM ? '0px' : 'unset',
    width: '100%',
    paddingBottom: isSM ? '0px' : '20px',
  },
  mainInformationContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  tagsTitle: {
    fontWeight: 600,
    fontSize: '20px',
    color: '#232B35',
    marginTop: '30px',
    marginBottom: '15px',
  },
  boardMeetingUpperBarText: {
    fontWeight: 'bold',
    fontSize: '14px',
  },
  generalMargin: {
    marginRight: '8px',
    marginBottom: '5px',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  boardsContainer: {
    '& > *': {},
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
  },
  biographyText: {
    whiteSpace: 'pre-wrap',
    fontSize: '16px',
    fontWeight: 400,
  },
  boardMeetingUpperBar: {
    backgroundColor: '#2C73F6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    height: !isSM ? '50px' : '40%',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    padding: { xxs: '15px', xs: '20px' },
    margin: { xxs: '12px -16px 0px', xs: '12px -40px 0px' },
  },
  p2pUpperBar: {
    backgroundColor: '#24A0A3',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#ffffff',
    height: '50px',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    padding: { xxs: '15px', xs: '0px' },
    margin: { xxs: '12px -20px 0px', xs: '12px -40px 0px' },
  },
  benefitsContainer: {
    display: 'flex',
    flexDirection: isSM ? 'column' : 'row',
    justifyContent: 'left',
    marginTop: '20px',
    marginBottom: '10px',
  },
  benefitItem: {
    border: '1px solid #EFF0F7',
    borderRadius: '12px',
    height: '100%',
    padding: '24px',
  },
  benefitsDetailsText: {
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#646D7A',
  },
  benefitsCategoryText: {
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.43px',
    color: '#232B35',
    whiteSpace: 'nowrap',
    marginBottom: '11px',
  },
});

const CompanyOpportunitySlide = (props) => {
  let {
    company,
    skills,
    companyMembers,
    handleClickAway,
    hidePostSection,
    isFreemiumCompany,
    isAcPlusCompany,
    invitation,
    flareTagsFlag,
  } = props;

  const { type, accountDetails } = useAccount();
  const p2pFlag = company.boardType == 'P2P';
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down([1230]));
  const disabledCompany =
    company.opportunityStage === 'dormant' ||
    company.opportunityStage === 'pending_cancellation' ||
    company.boardOwnerStatus === 'inactive' ||
    company.boardOwnerStatus === 'paused';
  const classes = useStyles(isSM, disabledCompany);
  const [companyCEO, setCompanyCEO] = useState('');
  const [showUpperBar] = useState(
    (company.upcomingBoardMeeting &&
      !isDateExpired(company.upcomingBoardMeeting)) ||
      company.boardType == 'P2P'
  );
  const [isJoined, setIsJoined] = useState(
    accountDetails &&
      accountDetails.boards &&
      accountDetails.boards.memberBoards
      ? accountDetails.boards.memberBoards.some((bc) => bc.id === company.id)
      : false
  );

  const featuredBenefits = useMemo(() => {
    if (company.benefits && company.benefits.length) {
      return company.benefits.filter((b) => b.featured);
    }
    return [];
  }, []);

  useEffect(() => {
    if (companyMembers.length > 0) {
      setCompanyCEO(companyMembers[0]);
    }
  }, [companyMembers]);

  function renderCompanyBiographyText() {
    if (!company) return;
    if (!company.storyAndMission) return;
    return (
      <>
        <Typography variant="body1" sx={classes.biographyText}>
          {company.storyAndMission}
        </Typography>
      </>
    );
  }

  const desiredSkills = company.desiredSkillsTagIds
    ? company.desiredSkillsTagIds
    : [];

  skills = skills
    ? skills.filter((skill) => desiredSkills.includes(skill.id))
    : [];

  function conditionallyRenderUpperBar() {
    if (p2pFlag) {
      return (
        <Box sx={classes.p2pUpperBar}>
          <Typography variant="body1" sx={classes.boardMeetingUpperBarText}>
            Joining a Peer Advisor Board will not count towards the number of
            board seats provided by your membership
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={classes.boardMeetingUpperBar}>
        <Typography variant="body1" sx={classes.boardMeetingUpperBarText}>
          {company.joinPageBannerText ||
            'Companies seek diverse opinions and may be outside your usual industry. You can join or leave boards at any time to see which ones are a fit.'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {disabledCompany && (
        <Box
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            zIndex: '10',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              backgroundColor: '#F9F9F9',
              opacity: '0.7',
              zIndex: '-1',
            },
          }}
        >
          <Box
            sx={{
              backgroundColor: 'black',
              height: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '14px',
              position: 'relative',
              borderRadius: '30px',
              textAlign: 'center',
              margin: { xxs: '0px 28px', sm: '0px' },
            }}
          >
            <Typography color="white" sx={{ fontSize: '16px' }}>
              This company is not currently accepting new advisors.
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: { xxs: '55%', xs: '50%', sm: '180px' },
              right: { xxs: null, xs: '0px', sm: '36px' },
            }}
          >
            <LeaveBoardButton
              company={company}
              handleClickAway={handleClickAway}
              isFreemiumCompany={isFreemiumCompany}
              isAcPlusCompany={isAcPlusCompany}
              isJoined={isJoined}
              setIsJoined={setIsJoined}
            />
          </Box>
        </Box>
      )}
      <Paper sx={classes.card}>
        <div style={classes.companyInformationSectionContainer}>
          <div style={classes.mainInformationContainer}>
            <CompanySlideHeader
              invitation={invitation}
              handleClickAway={handleClickAway}
              company={company}
              showUpperBar={showUpperBar}
              companyCEO={companyCEO}
              p2pFlag={p2pFlag}
              isFreemiumCompany={isFreemiumCompany}
              isAcPlusCompany={isAcPlusCompany}
              isJoined={isJoined}
            />
          </div>
          {!p2pFlag && !isFreemiumCompany && (
            <>
              {isAcPlusCompany && (!skills || skills.length == 0) ? null : (
                <Typography variant="body1" sx={classes.tagsTitle}>
                  {t('SEEKING-GUIDANCE-ON-LABEL')}
                </Typography>
              )}
              <div className="skillTagBlock" style={classes.tagsContainer}>
                {skills.map((skill, index) => (
                  <Box sx={classes.generalMargin} key={index}>
                    <Tag label={skill.label} />
                  </Box>
                ))}
              </div>
            </>
          )}
          {featuredBenefits && featuredBenefits.length ? (
            <div id="benefitsSection">
              <Typography variant="body1" sx={classes.tagsTitle}>
                {flareTagsFlag && '🎁 '}Potential Perks & Benefits for Board
                Members
              </Typography>

              <Grid container style={classes.benefitsContainer}>
                {featuredBenefits &&
                  featuredBenefits.map((benefit, index) => (
                    <Grid item mb="16px" key={index} xxs={12} md={4}>
                      <BenefitCard
                        benefit={benefit}
                        index={index}
                        itemsPerRow={3}
                        flareTagsFlag={flareTagsFlag}
                      />
                    </Grid>
                  ))}
              </Grid>
            </div>
          ) : null}
          {flareTagsFlag && (
            <div id="staticBenefitsSection">
              <Typography
                variant="body1"
                sx={{ ...classes.tagsTitle, marginTop: '16px' }}
              >
                🚀 More Benefits of Becoming a Board Member
              </Typography>
              <Grid container style={classes.benefitsContainer}>
                {[
                  {
                    percentage: '44',
                    backgroundColor: '#D7F1FD',
                    text: 'Executives serving on an advisory board are 44% more likely to be promoted to a C-Suite position.',
                  },
                  {
                    percentage: '13',
                    backgroundColor: '#DBF7EB',
                    text: 'Professionals with advisory board experience saw up to 13% boost in annual salary and earning power.',
                  },
                  {
                    percentage: '60',
                    backgroundColor: '#FFEED6',
                    text: 'Board members on AdvisoryCloud see a 60% increase in available networking connections on average.',
                  },
                ].map((content, index) => (
                  <Grid item mb="16px" key={index} xxs={12} md={4}>
                    <StaticTile {...content} itemsPerRow={3} index={index} />
                  </Grid>
                ))}
              </Grid>
            </div>
          )}

          {conditionallyRenderUpperBar()}
          {!isJoined && (
            <HowItWorks
              button={
                <BoardJoinButton
                  invitation={invitation}
                  boardId={company?.id}
                  company={company}
                  companyCEO={companyCEO}
                  staticClass="joinButtonHowItWorks"
                />
              }
            />
          )}

          {!p2pFlag ? (
            <>
              <Typography variant="body1" sx={{ ...classes.tagsTitle }}>
                {t('LEADERSHIP-LABEL')}
              </Typography>
              <div style={classes.boardsContainer}>
                {companyMembers ? (
                  <>
                    {companyMembers.map((member) => (
                      <Box className={classes.generalMargin} key={member.id}>
                        <CompanyContact
                          contact={member}
                          customAvatar={() => (
                            <Avatar
                              sx={{ width: '40px', height: '40px' }}
                              src={
                                member.contactLogoId
                                  ? member.contactLogoLocation
                                  : DefaultAdvisorAvatar
                              }
                            />
                          )}
                        />
                      </Box>
                    ))}
                  </>
                ) : null}
              </div>
            </>
          ) : null}
          <Typography variant="body1" sx={{ ...classes.tagsTitle }}>
            More about {company.displayName}
          </Typography>
          {renderCompanyBiographyText()}
          {!isJoined && (
            <Box>
              <Typography
                variant="body1"
                sx={{
                  ...classes.tagsTitle,
                  textAlign: 'center',
                  fontWeight: 700,
                }}
              >
                Join the {company.displayName} advisory board
              </Typography>
              <BoardJoinButton
                invitation={invitation}
                boardId={company?.id}
                company={company}
                companyCEO={companyCEO}
                // staticClass="joinButtonBottom"
              />
            </Box>
          )}
        </div>

        {/* {!hidePostSection &&
          type === AccountType.ADVISOR &&
          !isFreemiumCompany &&
          !isAcPlusCompany && (
            <CompanyBoardPreview
              invitation={invitation}
              company={company}
              companyId={company.id}
              companyName={company.displayName}
              companyImage={`https://${company.image.location}`}
              companyCEO={companyCEO}
              isJoined={isJoined}
            />
          )} */}
        {!hidePostSection &&
          type === AccountType.ADVISOR &&
          isAcPlusCompany && (
            <AcPlusPreview
              company={company}
              companyId={company.id}
              companyName={company.displayName}
              companyImage={`https://${company.image.location}`}
              companyCEO={companyCEO}
            />
          )}

        <LeaveBoardButton
          company={company}
          handleClickAway={handleClickAway}
          isFreemiumCompany={isFreemiumCompany}
          isAcPlusCompany={isAcPlusCompany}
          isJoined={isJoined}
          setIsJoined={setIsJoined}
        />
      </Paper>
    </>
  );
};

export default CompanyOpportunitySlide;
